<template>
  <md-card>
    <div class="card">
      <md-card-header>
        <items-view :title="'Guidance Details: ' + guidance.name">
          <item-view label="Guidance Number" :value="guidance.guidance_number"></item-view>
          <item-view label="Guidance Duration" :value="guidance.duration"></item-view>
          <item-view label="Guidance Type" :value="guidance.guidance_type"></item-view>
          <item-view label="Guidance Date" :value="guidance.date_range"></item-view>
          <item-view label="Port" :value="getPortName()"></item-view>
          <item-view label="Department" :value="getDepartmentName()"></item-view>
          <item-view label="Vendor" :value="getVendorName()"></item-view>
          <item-view label="Tariff" :value="getTariffName()"></item-view>
          <item-view label="~hours" :value="formatHours(getTotalHours())"></item-view>
          <item-view label="~Labours" :value=" formatNumber(getTotalLabourers())"></item-view>
          <item-view label="~Value" :value="formatCurrency(getTotalAmount())"></item-view>
        </items-view>

        <!-- Step 1: Select Shifts -->
        <div class="md-layout md-gutter mt-4">
          <div class="md-layout-item md-small-size-100">
            <label class="section-label">Step 1: Select Shifts for this Guidance</label>
            <select-input
                v-model="selected_shifts"
                label="Select Shifts"
                :options="shifts"
                :error="apiValidationErrors.department_id"
                track-by="id"
                name="name"
                :multiple="true"
                :taggable="false"
                :reduce-to-value="false"
                @selecting="addShiftToGuidance"
                @removing="removeShiftFromGuidance"
            />
            <div class="help-text">Select all shifts that will be used for this guidance</div>
          </div>
        </div>
      </md-card-header>

      <!-- Action Buttons -->
      <div class="action-bar">
        <md-button class="md-dense md-raised md-primary" @click="addWorkPoint">
          <md-icon>add</md-icon> Add Work Point
        </md-button>
        <md-button class="md-dense md-raised md-secondary" @click="goBack">
          <md-icon>arrow_back</md-icon> Go Back
        </md-button>
      </div>

      <!-- Work Points List -->
      <div v-for="(workPoint, wpIndex) in workPoints" :key="'wp-' + wpIndex" class="workpoint-card">
        <md-card class="workpoint-card-content">
          <md-card-header>
            <div class="workpoint-header">
              <h4>Work Point {{ wpIndex + 1 }}</h4>
              <md-button class="md-icon-button md-danger" @click="removeWorkPoint(wpIndex)">
                <md-icon>delete</md-icon>
              </md-button>
            </div>
          </md-card-header>

          <md-card-content>
            <!-- Select Workplace -->
            <div class="md-layout md-gutter">
              <div class="md-layout-item md-size-50">
                <label class="section-label">Step 2: Select Workplace</label>
                <select-input
                    v-model="workPoint.workplace"
                    label="Workplace/Work Point"
                    :options="filteredWorkplaces"
                    :error="apiValidationErrors.workplace_id"
                    track-by="id"
                    name="name"
                    :taggable="false"
                    :reduce-to-value="false"
                    @selecting="updateWorkPoint($event, wpIndex)"
                />
              </div>

              <div class="md-layout-item md-size-50">
                <label class="section-label">Optional: Select Vessel</label>
                <select-input
                    v-model="workPoint.vessel"
                    label="Vessel (if any)"
                    :options="vessels"
                    :error="apiValidationErrors.vessel"
                    track-by="id"
                    name="name"
                    :taggable="false"
                    :reduce-to-value="false"
                    @selecting="updateWorkPointVessel($event, wpIndex)"
                />
              </div>
            </div>

            <!-- Activities Section -->
            <div class="activities-section">
              <div class="section-header">
                <label class="section-label">Step 3: Add Activities</label>
                <md-button class="md-small md-raised md-primary" :disabled="!workPoint.workplace_id" @click="addActivity(wpIndex)">
                  <md-icon>add</md-icon> Add Activity
                </md-button>
              </div>

              <div v-if="!workPoint.workplace_id" class="warning-box">
                Please select a workplace first before adding activities
              </div>

              <!-- Activities Table -->
              <div v-if="workPoint.activities && workPoint.activities.length">
                <md-table>
                  <md-table-row>
                    <md-table-head md-numeric>#</md-table-head>
                    <md-table-head class="cell-50">Activity</md-table-head>
                    <md-table-head v-for="shift in selected_shifts" :key="'head-' + shift.id" class="cell-10" md-numeric>
                      {{ shift.name }}
                      <span class="shift-time">{{ shift.start_time }} - {{ shift.end_time }}</span>
                    </md-table-head>
                    <md-table-head>Actions</md-table-head>
                  </md-table-row>

                  <template v-for="(activity, actIndex) in workPoint.activities">
                    <!-- Main Activity Row -->
                    <md-table-row :key="'act-' + wpIndex + '-' + actIndex" class="activity-row">
                      <md-table-cell>
                        <md-button @click="toggleExceptions(wpIndex, actIndex)"
                                   class="md-icon-button md-white md-size"
                                   :class="getExceptionButtonClass(activity)"
                                   :title="getExceptionButtonTitle(activity)">
                          <md-icon>
                            {{ isExceptionVisible(wpIndex, actIndex) ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
                          </md-icon>
                          {{ actIndex + 1 }}
                          <span v-if="activity.exceptions && activity.exceptions.length > 0" class="exception-count">
                            {{ activity.exceptions.length }}
                          </span>
                        </md-button>
                      </md-table-cell>

                      <md-table-cell>
                        <select-input
                            v-model="activity.activity"
                            label="Select Activity"
                            :options="getWorkplaceActivities(workPoint.workplace)"
                            :error="getActivityErrors(activity)"
                            track-by="id"
                            name="name"
                            :taggable="false"
                            :reduce-to-value="false"
                            :disabled="!workPoint.workplace_id"
                            @selecting="selectActivity($event, wpIndex, actIndex)"
                        />
                      </md-table-cell>

                      <!-- Labourer Count Inputs for each Shift -->
                      <md-table-cell v-for="shift in selected_shifts" :key="'cell-' + shift.id + '-' + actIndex">
                        <div class="shift-input-container">
                          <md-field class="shift-field">
                            <md-input
                                type="number"
                                :value="getLabourerCountValue(wpIndex, actIndex, shift.id)"
                                @change="updateLabourerCountValue($event, wpIndex, actIndex, shift.id)"
                                @focus="$event.target.select()"
                                :min="0"
                                placeholder="Count"
                            />
                          </md-field>
                          <md-checkbox
                              :checked="getRequestTypeValue(wpIndex, actIndex, shift.id) === 'Provision'"
                              class="provision-checkbox"
                              @change="updateRequestTypeValue($event, wpIndex, actIndex, shift.id)"
                          >
                            PR
                          </md-checkbox>
                        </div>
                      </md-table-cell>

                      <md-table-cell>
                        <div class="action-buttons">
                          <md-button class="md-icon-button md-raised md-danger" @click="removeActivity(wpIndex, actIndex)" title="Remove Activity">
                            <md-icon>delete</md-icon>
                          </md-button>

                          <md-button v-if="isActivityUnsaved(activity)" class="md-icon-button md-raised md-primary" @click="saveActivity(wpIndex, actIndex)" title="Save Activity">
                            <md-icon>save</md-icon>
                          </md-button>

                          <md-progress-spinner v-if="activity.saving" class="md-info" :md-diameter="24" md-mode="indeterminate"></md-progress-spinner>
                          <span v-if="activity.saved && activity.saveText" class="saved-badge">Saved</span>

                          <!-- Exception Menu for non-daily guidance -->
                          <md-menu v-if="guidance.duration !== 'Day'">
                            <md-button class="md-icon-button" md-menu-trigger title="Add Exception">
                              <md-icon>event</md-icon>
                            </md-button>
                            <md-menu-content>
                              <md-menu-item @click="addException('Saturday', wpIndex, actIndex)">
                                <span>Saturday Exception</span>
                              </md-menu-item>
                              <md-menu-item @click="addException('Sunday', wpIndex, actIndex)">
                                <span>Sunday Exception</span>
                              </md-menu-item>
                              <md-menu-item @click="addException('Holiday', wpIndex, actIndex)">
                                <span>Holiday Exception</span>
                              </md-menu-item>
                            </md-menu-content>
                          </md-menu>
                        </div>
                      </md-table-cell>
                    </md-table-row>

                    <!-- Exceptions Section -->
                    <template v-if="isExceptionVisible(wpIndex, actIndex)">
                      <md-table-row v-for="(exception, excIndex) in activity.exceptions"
                                    class="exception-row"
                                    :key="'exception-' + wpIndex + '-' + actIndex + '-' + excIndex">
                        <md-table-cell>
                          <md-icon class="exception-icon">warning</md-icon>
                          {{ exception.day_type }}
                        </md-table-cell>
                        <md-table-cell>
                          <span class="exception-label">{{ exception.day_type }} Exception</span>
                        </md-table-cell>
                        <md-table-cell v-for="shift in selected_shifts" :key="'exc-cell-' + shift.id + '-' + excIndex">
                          <div class="shift-input-container">
                            <md-field class="shift-field">
                              <md-input
                                  type="number"
                                  :value="getExceptionLabourerCount(wpIndex, actIndex, excIndex, shift.id)"
                                  @change="updateExceptionLabourerCount($event, wpIndex, actIndex, excIndex, shift.id)"
                                  :min="0"
                                  placeholder="Count"
                              />
                            </md-field>
                            <md-checkbox
                                :checked="getExceptionRequestType(wpIndex, actIndex, excIndex, shift.id) === 'Provision'"
                                class="provision-checkbox"
                                @change="updateExceptionRequestType($event, wpIndex, actIndex, excIndex, shift.id)"
                            >
                              PR
                            </md-checkbox>
                          </div>
                        </md-table-cell>
                        <md-table-cell>
                          <md-button class="md-icon-button md-raised md-danger" @click="removeException(wpIndex, actIndex, excIndex)" title="Remove Exception">
                            <md-icon>delete</md-icon>
                          </md-button>
                        </md-table-cell>
                      </md-table-row>
                    </template>
                  </template>

                  <md-table-row class="table-footer-row">
                    <md-table-cell colspan="1"><strong>Total:</strong></md-table-cell>
                    <md-table-cell><strong>{{ getWorkPointTotal(wpIndex) }}</strong></md-table-cell>
                    <md-table-cell v-for="shift in selected_shifts" :key="'total-' + shift.id">
                      <strong>{{ getShiftTotalForWorkPoint(wpIndex, shift.id) }}</strong>
                    </md-table-cell>
                    <md-table-cell></md-table-cell>
                  </md-table-row>
                </md-table>
              </div>

              <div v-else-if="workPoint.workplace_id" class="empty-activities">
                <md-icon>info</md-icon>
                <p>No activities added yet. Click "Add Activity" to get started.</p>
              </div>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <!-- Bottom Actions -->
      <div v-if="workPoints.length > 0" class="action-bar-bottom">
        <md-button class="md-raised md-primary" @click="addWorkPoint">
          <md-icon>add</md-icon> Add Another Work Point
        </md-button>
        <md-button class="md-raised md-secondary" @click="goBack">
          <md-icon>arrow_back</md-icon> Go Back
        </md-button>
      </div>
    </div>
  </md-card>
</template>

<script>
// import { ValidationError } from '@/components';
import guidanceStatisticsMixin from '@/mixins/guidance-statistics-mixin';
import formMixin from '@/mixins/form-mixin';
import Swal from 'sweetalert2';
import ItemsView from '../../../components/Inputs/ItemsView';
import ItemView from '../../../components/Inputs/ItemView';
import SelectInput from '@/components/Inputs/SelectInput.vue';
// import GuidanceStatisticPanel from "@/pages/settings/guidances/components/GuidanceStatisticPanel.vue";

export default {
  name: 'ManageGuidanceShiftPage',
  components: {
    SelectInput,
    ItemView,
    ItemsView,
    // GuidanceStatisticPanel
  },
  mixins: [formMixin,guidanceStatisticsMixin],
  data() {
    return {
      activeTariff: {},
      selected_shifts: [],
      backup_shifts: [],
      allWorkplaces: [],
      shifts: [],
      vessels: [],
      workPoints: [],
      guidance: {
        id: null,
        name: null,
        guidance_number: null,
        duration: null,
        date_range: null,
        port: null,
        department: null,
        vendor: null,
        tariff: null,
        status: null
      },
      id: '',
      isLoading: false,
      exceptionSaving: false,
      visibleExceptions: {}
    };
  },

  computed: {
    filteredWorkplaces: function() {
      var self = this;
      console.log(this.guidance.port_id)
      if (!this.guidance.port_id || !this.allWorkplaces) {
        console.log(this.allWorkplaces? this.allWorkplaces.length : 0);
        this.notifyWarning('Port ID or Workplace List is empty. ')
        return this.allWorkplaces;
      }
      return this.allWorkplaces.filter(function(workplace) {
        return workplace.port_id === self.guidance.port_id;
      });
    }
  },

  created: function() {
    this.initialize();
  },

  methods: {
    initialize: async function() {
      var self = this;
      self.isLoading = true;
      try {
        await self.getGuidance();
        await self.getWorkplaces();
        await self.getVessels();
        await self.getShifts();
        await self.getTariff();
        self.processGuidanceWorkplaces();
      } catch (error) {

        self.notifyError('Failed to load page data');
      } finally {
        self.isLoading = false;
      }
    },

    getPortName: function() {
      return this.guidance.port ? this.guidance.port.name : '';
    },

    getDepartmentName: function() {
      return this.guidance.department ? this.guidance.department.name : '';
    },

    getVendorName: function() {
      return this.guidance.vendor ? this.guidance.vendor.name : '';
    },

    getTariffName: function() {
      return this.guidance.tariff ? this.guidance.tariff.name : '';
    },

    goBack: function() {
      var id = this.guidance.id;
      this.$router.push({ name: 'View Guidance', params: { id: id } });
    },

    getGuidance: async function() {
      var self = this;
      var id = this.$route.params.id;
      this.id = id;

      var params = {
        id: id,
        include: 'port,department,tariff,guidanceWorkplaces.workplace.activities,' +
            'guidanceWorkplaces.activityGuidances.activity,' +
            'guidanceWorkplaces.activityGuidances.exceptions,' +
            'guidanceWorkplaces.activityGuidances.activityShifts.shift,' +
            'guidanceWorkplaces.activityGuidances.activityTariff,' +
            'guidanceWorkplaces.activityGuidances.exceptions.activity,' +
            'guidanceWorkplaces.activityGuidances.exceptions.activityShifts.shift'
      };

      await this.$store.dispatch('guidances/view', params);
      this.guidance = this.$store.getters['guidances/guidance'];
      console.log('guidance:,',this.guidance)

      if (this.guidance.status !== '' && this.guidance.status !== 'Created') {
        Swal.fire('Reviewed Guidance', 'You cannot modify reviewed Guidance', 'info');
        self.goBack();
      }
    },

    getTariff: async function() {
      var self = this;
      var paramsTariff = { id: this.guidance.tariff.id, include: 'activities' };

      console.log('guidance:getTariff,',this.guidance.tariff)

      await this.$store.dispatch('tariffs/get', paramsTariff);
      this.activeTariff = this.$store.getters['tariffs/tariff'];

      if (!this.activeTariff) {
        Swal.fire('No Active Tariff', 'Please ask Admin/Accountant to create/activate one', 'error');
        self.goBack();
      }
    },

    getWorkplaces: async function() {
      await this.$store.dispatch('workplaces/list', { include: 'activities' });
      this.allWorkplaces = this.$store.getters['workplaces/list'] || [];
    },

    getVessels: async function() {
      await this.$store.dispatch('vessels/list');
      this.vessels = this.$store.getters['vessels/list'] || [];
    },

    getShifts: async function() {
      var self = this;
      await this.$store.dispatch('shifts/list',{filter:{is_active:'1'}});
      this.shifts = this.$store.getters['shifts/list'] || [];

      var shiftParams = { filter: { guidance: this.id } };
      await this.$store.dispatch('shifts/list', shiftParams);
      this.selected_shifts = this.$store.getters['shifts/list'] || [];
      this.backup_shifts = JSON.parse(JSON.stringify(this.selected_shifts));

      // Ensure all activities have shifts for selected shifts
      this.workPoints.forEach(function(wp) {
        wp.activities.forEach(function(activity) {
          self.ensureActivityShifts(activity);
        });
      });
    },

// Get labourer count value - Using shift.id for matching
    getLabourerCountValue: function(wpIndex, actIndex, shiftId) {
      try {
        // Check if workPoints exists and is an array
        if (!this.workPoints || !Array.isArray(this.workPoints)) {
          this.notifyError('workPoints is null or object')
          return 0;
        }

        var workPoint = this.workPoints[wpIndex];
        if (!workPoint) {
          this.notifyError('WorkPoint not found at index:');
          return 0;
        }

        var activities = workPoint.activities;
        if (!activities || !Array.isArray(activities)) {
          return 0;
        }

        var activity = activities[actIndex];
        if (!activity) {
          this.notifyError('Activity not found at index:');
          return 0;
        }

        var shifts = activity.activity_shifts;
        if (!shifts || !Array.isArray(shifts)) {
          return 0;
        }

        // Match by shift.id (UUID from API) instead of shift_id (numeric)
        var shiftData = shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        return shiftData ? shiftData.labourer_count : 0;
      } catch (error) {
        this.notifyError('Error getting labourer count:');
        return 0;
      }
    },

// Update labourer count value - Using shift.id for matching
    updateLabourerCountValue: function(event, wpIndex, actIndex, shiftId) {
      console.log('updateLabourerCountValue:')
      try {
        // Get the value
        var value = 0;
        if (event && typeof event === 'object' && event.target) {
          value = parseInt(event.target.value) || 0;
        } else if (typeof event === 'number') {
          value = event;
        } else if (typeof event === 'string') {
          value = parseInt(event) || 0;
        }

        // Check if workPoints exists and is an array
        if (!this.workPoints || !Array.isArray(this.workPoints)) {
          this.notifyError('workPoints is not an array:');
          return;
        }

        var workPoint = this.workPoints[wpIndex];
        if (!workPoint) {
          this.notifyError('WorkPoint not found at index:');
          return;
        }

        var activities = workPoint.activities;
        if (!activities || !Array.isArray(activities)) {
          console.log(activities);
          console.log('workPoints activities is null or object')

          return;
        }
        var activity = activities[actIndex];
        if (!activity) {
          this.notifyError('Activity not found at index:');
          return;
        }
        var shifts = activity.activity_shifts;
        if (!shifts || !Array.isArray(shifts)) {
          console.log('workPoints shifts is null or object')

          return;
        }

        var shiftData = shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        if (shiftData) {
          // Update the value
          this.$set(shiftData, 'labourer_count', value);
          // Update in backend
          this.updateLabourerCount(wpIndex, actIndex, shiftId);
        }else{
          //create activity
          // var payload = {
          //   type: 'activityShifts',
          //       labourer_count: value,
          //     request_type:  'Open',
          //     shift_id: shiftId,
          //     activity_guidance_id: shiftData.activity_guidance_id,
          //     day_type: activity.day_type || 'Weekday'
          // }

        }
      } catch (error) {
        this.notifyError('Error updating labourer count value:', error)
      }
    },

// Get request type value - Using shift.id for matching
    getRequestTypeValue: function(wpIndex, actIndex, shiftId) {
      try {
        if (!this.workPoints || !Array.isArray(this.workPoints)) {
          return 'Open';
        }

        var workPoint = this.workPoints[wpIndex];
        if (!workPoint) return 'Open';

        var activities = workPoint.activities;
        if (!activities || !Array.isArray(activities)) return 'Open';

        var activity = activities[actIndex];
        if (!activity) return 'Open';

        var shifts = activity.activity_shifts;
        if (!shifts || !Array.isArray(shifts)) return 'Open';

        // Match by shift.id (UUID from API) instead of shift_id (numeric)
        var shiftData = shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        return shiftData ? shiftData.request_type : 'Open';
      } catch (error) {
        this.notifyError('Error getting request type:', error);
        return 'Open';
      }
    },

// Update request type value - Using shift.id for matching
    updateRequestTypeValue: function(event, wpIndex, actIndex, shiftId) {
      try {
        var value = 'Open';
        if (event && typeof event === 'object' && event.target) {
          value = event.target.checked ? 'Provision' : 'Open';
        } else if (typeof event === 'boolean') {
          value = event ? 'Provision' : 'Open';
        }

        if (!this.workPoints || !Array.isArray(this.workPoints)) {
          return;
        }

        var workPoint = this.workPoints[wpIndex];
        if (!workPoint) return;

        var activities = workPoint.activities;
        if (!activities || !Array.isArray(activities)) return;

        var activity = activities[actIndex];
        if (!activity) return;

        var shifts = activity.activity_shifts;
        if (!shifts || !Array.isArray(shifts)) return;

        // Match by shift.id (UUID from API) instead of shift_id (numeric)
        var shiftData = shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        if (shiftData) {
          this.$set(shiftData, 'request_type', value);
          this.updateLabourerCount(wpIndex, actIndex, shiftId);
        }
      } catch (error) {
        this.notifyError('Error updating request type:', error);
      }
    },

    updateLabourerCount: async function(wpIndex, actIndex, shiftId) {
      console.log('updateLabourerCount', wpIndex, actIndex, shiftId);
      try {
        if (!this.workPoints || !Array.isArray(this.workPoints)) return;

        var workPoint = this.workPoints[wpIndex];
        if (!workPoint) return;

        var activity = workPoint.activities[actIndex];
        if (!activity) return;

        var shiftData = activity.activity_shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        if (!shiftData) return;
        console.log('<<<<<< Shift data >>>>>>>>')
        console.log(shiftData);
        console.log('<<<<<<<<<< shift data >>>>>>>>>>');

        // Set saving state
        this.$set(activity, 'saving', true);

        var payload = {
          type: 'activityShifts',
          labourer_count: parseInt(shiftData.labourer_count) || 0,
          request_type: shiftData.request_type || 'Open',
          shift_id: shiftData.shift.id,
          activity_guidance_id: shiftData.activity_guidance_id,
          day_type: activity.day_type || 'Weekday'
        };

        if (shiftData.id) {
          payload.id = shiftData.id;
          await this.$store.dispatch('activityShifts/update', payload);

          // Refresh just this shift
          var shiftParams = {
            id: shiftData.id,
            include: 'shift'
          };
          await this.$store.dispatch('activityShifts/view', shiftParams);
          var refreshedShift = this.$store.getters['activityShifts/activityShift'];
          if (refreshedShift) {
            this.$set(shiftData, 'labourer_count', refreshedShift.labourer_count);
            this.$set(shiftData, 'request_type', refreshedShift.request_type);
          }

        } else {
          payload.activity_guidance_id = activity.id;
          await this.$store.dispatch('activityShifts/add', payload);
          var saved = this.$store.getters['activityShifts/activityShift'];
          this.$set(shiftData, 'id', saved.id);
          this.$set(shiftData, 'uuid', saved.id);
        }

        this.updateAllTotals();
        this.$set(activity, 'saving', false);
        this.notifySuccess('Labourer count saved');

      } catch (error) {
        console.error('Error updating labourer count:', error);
        if (activity) {
          this.$set(activity, 'saving', false);
        }
        this.handleApiError(error);
      }
    },

    processGuidanceWorkplaces: function() {
      var self = this;
      var guidanceWorkplaces = this.guidance.guidanceWorkplaces || [];

      this.workPoints = [];

      guidanceWorkplaces.forEach(function(gw) {
        var workPoint = {
          id: gw.id,
          uuid: gw.id,
          workplace_id: gw.workplace_id,
          workplace: gw.workplace,
          vessel: gw.vessel,
          vessel_id: gw.vessel_id,
          guidance_id: gw.guidance_id,
          activities: [],
          labourer_count: 0
        };

        // Process activities
        var activityGuidances = gw.activityGuidances || [];
        // console.log('processGuidanceWorkplaces:activityGuidances', activityGuidances);
        activityGuidances.forEach(function(ag) {
          if (ag.exception_id != null) return;
          var activity = {
            id: ag.id,
            uuid: ag.id,
            activity_id: ag.activity_id,
            activity: ag.activity,
            activityTariff: ag.activityTariff,
            day_type: ag.day_type || 'Weekday',
            activity_shifts: [],
            exceptions: ag.exceptions || [],
            saved: true,
            saveText: false,
            saving: false,
            labourer_count: 0
          };

          // Process activity shifts
          var activityShifts = ag.activityShifts || [];
          activityShifts.forEach(function(ash) {
            activity.activity_shifts.push({
              id: ash.id,
              uuid: ash.id,
              activity_guidance_id: ash.activity_guidance_id,
              shift_id: ash.shift_id,
              shift: ash.shift,
              labourer_count: ash.labourer_count || 0,
              request_type: ash.request_type || 'Open',
              day_type: ash.day_type || activity.day_type
            });
          });

          // Process exceptions
          if (ag.exceptions && ag.exceptions.length) {
            activity.exceptions = ag.exceptions.map(function(exc) {
              var exceptionObj = {
                id: exc.id,
                uuid: exc.id,
                day_type: exc.day_type,
                activity_id: exc.activity_id,
                activity_shifts: []
              };

              var excShifts = exc.activityShifts || [];
              excShifts.forEach(function(esh) {
                exceptionObj.activity_shifts.push({
                  id: esh.id,
                  uuid: esh.id,
                  shift_id: esh.shift_id,
                  activity_guidance_id: esh.activity_guidance_id,
                  shift: esh.shift,
                  labourer_count: esh.labourer_count || 0,
                  request_type: esh.request_type || 'Open',
                  day_type: exc.day_type
                });
              });

              return exceptionObj;
            });
          }

          workPoint.activities.push(activity);
        });

        self.workPoints.push(workPoint);
      });

      this.updateAllTotals();
    },

// Get shift total for work point - Using shift.id for matching
    getShiftTotalForWorkPoint: function(wpIndex, shiftId) {
      var total = 0;

      if (!this.workPoints || !Array.isArray(this.workPoints)) {
        return 0;
      }

      var workPoint = this.workPoints[wpIndex];
      if (!workPoint || !workPoint.activities || !Array.isArray(workPoint.activities)) {
        return 0;
      }

      for (var i = 0; i < workPoint.activities.length; i++) {
        var activity = workPoint.activities[i];
        if (activity.activity_shifts && Array.isArray(activity.activity_shifts)) {
          for (var j = 0; j < activity.activity_shifts.length; j++) {
            var shiftData = activity.activity_shifts[j];
            // Match by shift.id (UUID)
            if (shiftData.shift && shiftData.shift.id === shiftId) {
              total += parseInt(shiftData.labourer_count) || 0;
              break;
            }
          }
        }
      }

      return total;
    },

    ensureActivityShifts: function(activity) {
      var self = this;
      console.log('<<<<<<<<<<<shift>>>>>>>>>>>')

      if (!activity.activity_shifts) {
        console.log('no shift')
        console.log(activity);
        activity.activity_shifts = [];
      }

      this.selected_shifts.forEach(function(shift) {
        // Check by shift.id (UUID) instead of shift_id
        var existing = activity.activity_shifts.find(function(s) {
          return s.shift && s.shift.id === shift.id;
        });
        console.log('<<<<<<<<<<<shift>>>>>>>>>>>')
        console.log(shift)
        console.log('<<<<<<<<<<<shift>>>>>>>>>>>')

        if (!existing) {
          activity.activity_shifts.push({
            shift_id: shift.id,
            shift: shift,
            activity_guidance_id: shift.activity_guidance_id,
            labourer_count: 0,
            request_type: 'Open',
            day_type: activity.day_type || 'Weekday'
          });
        }
      });
    },

    getWorkplaceActivities: function(workplace) {
      if (!workplace || !workplace.id || !workplace.activities) {
        this.notifyError('workplace is null empty activities returned')
        return [];
      }

      var validActivityIds = this.activeTariff.activities.map(function(a) {
        return a.id;
      });
      return workplace.activities.filter(function(activity) {
        return validActivityIds.indexOf(activity.id) !== -1;
      });
    },

    getActivityErrors: function(activity) {
      return activity.errors ? activity.errors.activity_id : [];
    },

    addShiftToGuidance: async function(shift) {
      if (!shift || !shift.id) return;

      var activityShift = {
        day_type: 'Weekday',
        shift_id: shift.id,
        labourer_count: 0,
        request_type: 'AddShift',
        id: this.id
      };

      await this.manageShift(activityShift, 'add');
    },

    removeShiftFromGuidance: async function(shift) {
      var self = this;
      if (!shift || !shift.id) return;

      var result = await Swal.fire({
        title: 'Remove Shift?',
        text: 'Removing "' + shift.name + '" will delete all labourer counts for this shift. Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel'
      });

      if (result.value) {
        var activityShift = {
          shift_id: shift.id,
          request_type: 'RemoveShift',
          id: this.id
        };
        await this.manageShift(activityShift, 'remove');
      } else {
        this.selected_shifts = JSON.parse(JSON.stringify(this.backup_shifts));
      }
    },

    manageShift: async function(activityShift, action) {
      var self = this;
      try {
        this.isLoading = true;
        await this.$store.dispatch('guidances/manageShift', activityShift);

        await this.getGuidance();
        await this.processGuidanceWorkplaces();

        var shiftParams = { filter: { guidance: this.id } };
        await this.$store.dispatch('shifts/list', shiftParams);

        var shifts = this.$store.getters['shifts/list'];
        if (shifts && shifts.length > 0) {
          this.selected_shifts = shifts;
        }
        this.backup_shifts = JSON.parse(JSON.stringify(this.selected_shifts));

        this.updateAllTotals();
        this.notifySuccess('Shift ' + (action === 'add' ? 'added' : 'removed') + ' successfully');
      } catch (error) {
        this.notifyError('Error managing shift:', error);
        this.handleApiError(error);
      } finally {
        this.isLoading = false;
      }
    },

    addWorkPoint: function() {
      var newWorkPoint = {
        workplace: null,
        workplace_id: null,
        guidance_id: this.guidance.id,
        activities: [],
        vessel: null,
        vessel_id: null,
        labourer_count: 0
      };
      this.workPoints.push(newWorkPoint);
    },

    updateWorkPoint: async function(workplace, wpIndex) {
      if (!workplace || !workplace.id) return;

      // Check for duplicate
      var duplicate = false;
      for (var i = 0; i < this.workPoints.length; i++) {
        if (i !== wpIndex && this.workPoints[i].workplace && this.workPoints[i].workplace.id === workplace.id) {
          duplicate = true;
          break;
        }
      }

      if (duplicate) {
        await this.notifyError('This workplace has already been added!');
        return;
      }

      // Check if workplace has valid activities
      var validActivities = this.getWorkplaceActivities(workplace);
      if (validActivities.length === 0) {
        await this.notifyError('No activities found for this workplace matching the current tariff!');
        return;
      }

      var oldWorkplace = this.workPoints[wpIndex].workplace;
      this.workPoints[wpIndex].workplace = workplace;
      this.workPoints[wpIndex].workplace_id = workplace.id;

      var saved = await this.saveGuidanceWorkplace(wpIndex);
      if (!saved) {
        this.workPoints[wpIndex].workplace = oldWorkplace;
        this.workPoints[wpIndex].workplace_id = oldWorkplace ? oldWorkplace.id : null;
      }
    },

    updateWorkPointVessel: async function(vessel, wpIndex) {
      this.workPoints[wpIndex].vessel = vessel;
      this.workPoints[wpIndex].vessel_id = vessel ? vessel.id : null;

      if (this.workPoints[wpIndex].workplace_id) {
        await this.saveGuidanceWorkplace(wpIndex);
      }
    },

    saveGuidanceWorkplace: async function(wpIndex) {
      var workPoint = this.workPoints[wpIndex];

      try {
        var payload = {
          type: 'guidanceWorkplaces',
          workplace_id: workPoint.workplace.id,
          guidance_id: this.guidance.id,
          vessel_id: workPoint.vessel_id
        };

        if (workPoint.id) {
          payload.id = workPoint.id;
          await this.$store.dispatch('guidanceWorkplaces/update', payload);
        } else {
          await this.$store.dispatch('guidanceWorkplaces/add', payload);
          var saved = this.$store.getters['guidanceWorkplaces/guidanceWorkplace'];
          workPoint.id = saved.id;
        }

        this.notifySuccess('Workplace saved successfully');
        return true;
      } catch (error) {
        this.notifyException(error);
        return false;
      }
    },

    addActivity: function(wpIndex) {
      var self = this;
      var workPoint = this.workPoints[wpIndex];

      if (!workPoint.workplace_id) {
        this.notifyError('Please select a workplace first!');
        return;
      }
      var newActivity = {
        activity: null,
        activity_id: null,
        guidance_workplace_id: workPoint.id,
        workplace_id: workPoint.workplace_id,
        day_type: 'Weekday',
        type: 'activityGuidances',
        activity_shifts: [],
        exceptions: [],
        saved: false,
        saveText: false,
        saving: false
      };

      this.selected_shifts.forEach(function(shift) {
        newActivity.activity_shifts.push({
          shift: shift,
          labourer_count: 0,
          request_type: 'Open',
          day_type: 'Weekday'
        });
      });

      this.workPoints[wpIndex].activities.push(newActivity);
    },

    selectActivity: async function(activity, wpIndex, actIndex) {
      if (!activity || !activity.id) return;

      // Check for duplicate
      var duplicate = false;
      var activities = this.workPoints[wpIndex].activities;
      for (var i = 0; i < activities.length; i++) {
        if (i !== actIndex && activities[i].activity && activities[i].activity.id === activity.id) {
          duplicate = true;
          break;
        }
      }

      if (duplicate) {
        await this.notifyError('This activity has already been added!');
        return;
      }

      this.workPoints[wpIndex].activities[actIndex].activity = activity;
      this.workPoints[wpIndex].activities[actIndex].activity_id = activity.id;
      this.workPoints[wpIndex].activities[actIndex].saved = false;

      await this.saveActivity(wpIndex, actIndex);
    },

    saveActivity: async function(wpIndex, actIndex) {
      var activity = this.workPoints[wpIndex].activities[actIndex];
      var workPoint = this.workPoints[wpIndex];

      if (!activity.activity_id) {
        await this.notifyError('Please select an activity');
        return;
      }

      if (this.selected_shifts.length === 0) {
        await this.notifyError('Please select at least one shift first');
        return;
      }

      activity.saving = true;

      try {
        var payload = {
          type: 'activityGuidances',
          activity_id: activity.activity_id,
          guidance_workplace_id: workPoint.id,
          day_type: activity.day_type || 'Weekday'
        };

        console.log('saveActivity payload:', payload);

        if (activity.id) {
          // Update existing activity
          payload.id = activity.id;
          await this.$store.dispatch('activityGuidances/update', payload);

          // Refresh the activity data
          var refreshParams = {
            id: activity.id,
            include: 'activityShifts,activityTariff,activityShifts.shift,exceptions.activityShifts.shift'
          };
          await this.$store.dispatch('activityGuidances/view', refreshParams);
          var refreshed = this.$store.getters['activityGuidances/activityGuidance'];

          // Update activity shifts
          if (refreshed.activityShifts) {
            activity.activity_shifts = refreshed.activityShifts.map(function(ash) {
              return {
                id: ash.id,
                uuid: ash.id,
                shift_id: ash.shift_id,
                shift: ash.shift,
                labourer_count: ash.labourer_count || 0,
                request_type: ash.request_type || 'Open',
                day_type: ash.day_type || activity.day_type
              };
            });
          }
        } else {
          // Create new activity
          await this.$store.dispatch('activityGuidances/add', payload);
          var saved = this.$store.getters['activityGuidances/activityGuidance'];

          // Fetch with relationships
          let agParams = {
            id: saved.id,
            include: 'activityShifts,activityTariff,activityShifts.shift'
          };

          await this.$store.dispatch('activityGuidances/view', agParams);
          var savedNew = this.$store.getters['activityGuidances/activityGuidance'];

          console.log('Saved activity with shifts:', savedNew);

          activity.id = savedNew.id;
          activity.uuid = savedNew.id;

          // Map the shifts properly
          if (savedNew.activityShifts && savedNew.activityShifts.length) {
            activity.activity_shifts = savedNew.activityShifts.map(function(ash) {
              return {
                id: ash.id,
                uuid: ash.id,
                shift_id: ash.shift_id,
                shift: ash.shift,
                labourer_count: ash.labourer_count || 0,
                request_type: ash.request_type || 'Open',
                day_type: ash.day_type || activity.day_type
              };
            });
          } else {
            // Fallback: create default shifts if none returned
            activity.activity_shifts = this.selected_shifts.map(function(shift) {
              return {
                shift_id: shift.id,
                shift: shift,
                labourer_count: 0,
                request_type: 'Open',
                day_type: activity.day_type || 'Weekday'
              };
            });
          }
        }

        activity.saved = true;
        activity.saveText = true;
        activity.saving = false;

        setTimeout(function() {
          activity.saveText = false;
        }, 2000);

        this.updateAllTotals();
        this.notifySuccess('Activity saved successfully');

      } catch (error) {
        console.error('Error saving activity:', error);
        activity.saved = false;
        activity.saving = false;
        this.handleApiError(error);
      }
    },


    isActivityUnsaved: function(activity) {
      return !activity.saved && !activity.saving;
    },

    removeActivity: async function(wpIndex, actIndex) {
      var self = this;
      var result = await Swal.fire({
        title: 'Remove Activity?',
        text: 'Are you sure you want to remove this activity?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel'
      });

      if (!result.value) return;

      var activity = this.workPoints[wpIndex].activities[actIndex];

      try {
        if (activity.id) {
          await this.$store.dispatch('activityGuidances/destroy', activity.id);
        }

        this.workPoints[wpIndex].activities.splice(actIndex, 1);
        this.notifySuccess('Activity removed successfully');
        this.updateAllTotals();
      } catch (error) {
        this.notifyError('Error removing activity:', error);
        this.handleApiError(error);
      }
    },

    removeWorkPoint: async function(wpIndex) {
      var self = this;
      var result = await Swal.fire({
        title: 'Remove Work Point?',
        text: 'This will remove all activities in this work point. Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel'
      });

      if (!result.value) return;

      var workPoint = this.workPoints[wpIndex];

      try {
        if (workPoint.id) {
          await this.$store.dispatch('guidanceWorkplaces/destroy', workPoint.id);
        }

        this.workPoints.splice(wpIndex, 1);
        this.notifySuccess('Work point removed successfully');
      } catch (error) {
        this.notifyError('Error removing work point:', error);
        this.handleApiError(error);
      }
    },

    updateAllTotals: function() {
      var self = this;
      this.workPoints.forEach(function(workPoint) {
        var workPointTotal = 0;

        if (workPoint.activities) {
          workPoint.activities.forEach(function(activity) {
            var activityTotal = 0;

            if (activity.activity_shifts) {
              activityTotal = activity.activity_shifts.reduce(function(sum, shift) {
                var count = parseInt(shift.labourer_count) || 0;
                return sum + count;
              }, 0);
            }

            activity.labourer_count = activityTotal;
            workPointTotal += activityTotal;
          });
        }

        workPoint.labourer_count = workPointTotal;
      });
    },

    getWorkPointTotal: function(wpIndex) {
      var workPoint = this.workPoints[wpIndex];
      if (!workPoint) return 0;
      return workPoint.labourer_count || 0;
    },

    handleApiError: function(error) {
      if (error.response && error.response.data && error.response.data.errors && error.response.data.errors.length) {
        var apiError = error.response.data.errors[0];
        this.notifyError(apiError.title || apiError.detail || 'An error occurred');
        this.setApiValidation(error.response.data.errors);
      } else {
        this.notifyError('An unexpected error occurred. Please try again.');
      }
    },

    // Exception visibility methods
    isExceptionVisible: function(wpIndex, actIndex) {
      var key = wpIndex + '_' + actIndex;
      return  this.visibleExceptions[key] === true;
    },

    toggleExceptions: function(wpIndex, actIndex) {
      var key = wpIndex + '_' + actIndex;
      this.$set(this.visibleExceptions, key, !this.visibleExceptions[key]);
    },

    // Exception methods
    addException: async function(dayType, wpIndex, actIndex) {
      var activity = this.workPoints[wpIndex].activities[actIndex];
      var workPoint = this.workPoints[wpIndex];

      // Check if exception already exists
      var exists = activity.exceptions.some(function(exc) {
        return exc.day_type === dayType;
      });

      if (exists) {
        await this.notifyError(dayType + ' exception already exists');
        return;
      }

      var newException = {
        day_type: dayType,
        exception_id: activity.id,
        activity_id: activity.activity_id,
        guidance_workplace_id: workPoint.id,
        type: 'activityGuidances',
        activity_shifts: []
      };

      // Create shift entries for the exception
      this.selected_shifts.forEach(function(shift) {
        newException.activity_shifts.push({
          shift_id: shift.id,
          shift: shift,
          labourer_count: 0,
          request_type: 'Open',
          day_type: dayType
        });
      });

      try {
        await this.$store.dispatch('activityGuidances/add', newException);
        var saved = this.$store.getters['activityGuidances/activityGuidance'];

        // Fetch the exception with its relationships
        var excParams = {
          id: saved.id,
          include: 'activityShifts,activityShifts.shift'
        };

        await this.$store.dispatch('activityGuidances/view', excParams);
        var savedWithShifts = this.$store.getters['activityGuidances/activityGuidance'];

        console.log('Saved exception with shifts:', savedWithShifts);

        // Process the saved exception with shifts
        var exceptionObj = {
          id: savedWithShifts.id,
          uuid: savedWithShifts.id,
          day_type: savedWithShifts.day_type,
          activity_id: savedWithShifts.activity_id,
          activity_shifts: []
        };

        var savedShifts = savedWithShifts.activityShifts || [];
        savedShifts.forEach(function(esh) {
          exceptionObj.activity_shifts.push({
            id: esh.id,
            uuid: esh.id,
            shift_id: esh.shift_id,
            shift: esh.shift,
            labourer_count: esh.labourer_count || 0,
            request_type: esh.request_type || 'Open',
            day_type: savedWithShifts.day_type
          });
        });

        activity.exceptions.push(exceptionObj);
        this.notifySuccess(dayType + ' exception added successfully');

        // Auto-expand exceptions section
        this.toggleExceptions(wpIndex, actIndex);

      } catch (error) {
        console.error('Error adding exception:', error);
        this.handleApiError(error);
      }
    },

    removeException: async function(wpIndex, actIndex, excIndex) {
      var self = this;
      var result = await Swal.fire({
        title: 'Remove Exception?',
        text: 'Are you sure you want to remove this exception? This will delete all labourer counts for this exception.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel'
      });

      if (!result.value) return;

      var activity = this.workPoints[wpIndex].activities[actIndex];
      var exception = activity.exceptions[excIndex];

      try {
        if (exception.id) {
          await this.$store.dispatch('activityGuidances/destroy', exception.id);
        }

        activity.exceptions.splice(excIndex, 1);
        this.notifySuccess('Exception removed successfully');

      } catch (error) {
        this.notifyError('Error removing exception:', error);
        this.handleApiError(error);
      }
    },

    // Exception labourer count methods
    getExceptionLabourerCount: function(wpIndex, actIndex, excIndex, shiftId) {
      try {
        var activity = this.workPoints[wpIndex];
        if (!activity) return 0;

        var act = activity.activities[actIndex];
        if (!act || !act.exceptions) return 0;

        var exception = act.exceptions[excIndex];
        if (!exception || !exception.activity_shifts) return 0;

        var shiftData = exception.activity_shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        return shiftData ? shiftData.labourer_count : 0;
      } catch (error) {
        this.notifyError('Error getting exception labourer count:', error);
        return 0;
      }
    },

    updateExceptionLabourerCount: function(event, wpIndex, actIndex, excIndex, shiftId) {
      try {
        var value = 0;
        if (event && typeof event === 'object' && event.target) {
          value = parseInt(event.target.value) || 0;
        } else if (typeof event === 'number') {
          value = event;
        } else if (typeof event === 'string') {
          value = parseInt(event) || 0;
        }

        var activity = this.workPoints[wpIndex];
        if (!activity) {
          this.notifyError('no activity')
          return;
        }

        var act = activity.activities[actIndex];
        if (!act || !act.exceptions) {
          this.notifyError('no exception on  act')
          return;
        }

        var exception = act.exceptions[excIndex];
        if (!exception || !exception.activity_shifts)
        {
          this.notifyError('no exception or  eActivityShifts')
          return;
        }

        var shiftData = exception.activity_shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        if (shiftData) {
          this.$set(shiftData, 'labourer_count', value);
          this.saveExceptionShift(wpIndex, actIndex, excIndex, shiftId);
        }else{
          let s = this.selected_shifts.find(function(s) { return s.id === shiftId });
          const exceptionShiftData = {
            type: 'activityShifts',
            labourer_count: value,
            request_type: 'Open',
            shift_id: shiftId,
            shift: s,
            activity_guidance_id: exception.id,
            day_type: exception.day_type
          };
          exception.activity_shifts.push(exceptionShiftData);
          this.saveExceptionShift(wpIndex, actIndex, excIndex, shiftId);
          this.notifyError('no shiftData in Exception')
        }
      } catch (error) {
        this.notifyError('Error updating exception labourer count:', error);
      }
    },

    getExceptionRequestType: function(wpIndex, actIndex, excIndex, shiftId) {
      try {
        var activity = this.workPoints[wpIndex];
        if (!activity) return 'Open';

        var act = activity.activities[actIndex];
        if (!act || !act.exceptions) return 'Open';

        var exception = act.exceptions[excIndex];
        if (!exception || !exception.activity_shifts) return 'Open';

        var shiftData = exception.activity_shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        return shiftData ? shiftData.request_type : 'Open';
      } catch (error) {
        this.notifyError('Error getting exception request type:', error);
        return 'Open';
      }
    },

    updateExceptionRequestType: function(event, wpIndex, actIndex, excIndex, shiftId) {
      try {
        var value = 'Open';
        if (event && typeof event === 'object' && event.target) {
          value = event.target.checked ? 'Provision' : 'Open';
        } else if (typeof event === 'boolean') {
          value = event ? 'Provision' : 'Open';
        }

        var activity = this.workPoints[wpIndex];
        if (!activity) return;

        var act = activity.activities[actIndex];
        if (!act || !act.exceptions) return;

        var exception = act.exceptions[excIndex];
        if (!exception || !exception.activity_shifts) return;

        var shiftData = exception.activity_shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        if (shiftData) {
          this.$set(shiftData, 'request_type', value);
          this.saveExceptionShift(wpIndex, actIndex, excIndex, shiftId);
        }
      } catch (error) {
        this.notifyError('Error updating exception request type:', error);
      }
    },

    saveExceptionShift: async function(wpIndex, actIndex, excIndex, shiftId) {
      try {
        var activity = this.workPoints[wpIndex].activities[actIndex];
        var exception = activity.exceptions[excIndex];

        var shiftData = exception.activity_shifts.find(function(s) {
          return s.shift && s.shift.id === shiftId;
        });

        if (!shiftData) {
          this.notifyError('inException save action. no shift data')
          return
        }


        var payload = {
          type: 'activityShifts',
          labourer_count: parseInt(shiftData.labourer_count) || 0,
          request_type: shiftData.request_type || 'Open',
          shift_id: shiftData.shift_id,
          day_type: exception.day_type
        };

        if (shiftData.id) {
          // Update existing shift
          payload.id = shiftData.id;
          await this.$store.dispatch('activityShifts/update', payload);
        } else {
          // Create new shift
          payload.activity_guidance_id = exception.id;
          await this.$store.dispatch('activityShifts/add', payload);
          var saved = this.$store.getters['activityShifts/activityShift'];
          this.$set(shiftData, 'id', saved.id);
          this.$set(shiftData, 'uuid', saved.id);
        }

        this.notifySuccess('Exception Labourer Count Saved');
        this.updateAllTotals();

      } catch (error) {
        console.error('Error saving exception shift:', error);
        this.notifyError('Error saving exception shift:');
        this.handleApiError(error);
      }
    },


    getExceptionButtonClass: function(activity) {
      if (activity.exceptions && activity.exceptions.length > 0) {
        return 'md-danger';
      }
      return '';
    },

    getExceptionButtonTitle: function(activity) {
      if (activity.exceptions && activity.exceptions.length > 0) {
        return activity.exceptions.length + ' exception(s) for this activity';
      }
      return 'No exceptions';
    },

    refreshActivity: async function(wpIndex, actIndex) {
      var activity = this.workPoints[wpIndex].activities[actIndex];

      if (!activity.id) return;

      try {
        var params = {
          id: activity.id,
          include: 'activityShifts,activityShifts.shift,exceptions.activityShifts.shift'
        };

        await this.$store.dispatch('activityGuidances/view', params);
        var refreshed = this.$store.getters['activityGuidances/activityGuidance'];

        // Update activity shifts
        if (refreshed.activityShifts) {
          activity.activity_shifts = refreshed.activityShifts.map(function(ash) {
            return {
              id: ash.id,
              uuid: ash.id,
              shift_id: ash.shift_id,
              shift: ash.shift,
              labourer_count: ash.labourer_count || 0,
              request_type: ash.request_type || 'Open',
              day_type: ash.day_type || activity.day_type
            };
          });
        }

        // Update exceptions
        if (refreshed.exceptions && refreshed.exceptions.length) {
          activity.exceptions = refreshed.exceptions.map(function(exc) {
            var exceptionObj = {
              id: exc.id,
              uuid: exc.id,
              day_type: exc.day_type,
              activity_id: exc.activity_id,
              activity_shifts: []
            };

            if (exc.activityShifts) {
              exceptionObj.activity_shifts = exc.activityShifts.map(function(esh) {
                return {
                  id: esh.id,
                  uuid: esh.id,
                  shift_id: esh.shift_id,
                  shift: esh.shift,
                  labourer_count: esh.labourer_count || 0,
                  request_type: esh.request_type || 'Open',
                  day_type: exc.day_type
                };
              });
            }

            return exceptionObj;
          });
        }

        this.updateAllTotals();

      } catch (error) {
        console.error('Error refreshing activity:', error);
      }
    },

    getTotalLabourers: function() {
      var stats = this.guidanceStatistics;
      return stats && stats.labourer_count ? stats.labourer_count : 0;
    },

    getTotalHours: function() {
      var stats = this.guidanceStatistics;
      return stats && stats.total_hours ? stats.total_hours : 0;
    },

    getTotalAmount: function() {
      var stats = this.guidanceStatistics;
      return stats && stats.total_amount ? stats.total_amount : 0;
    },

    formatCurrency: function(amount) {
      if (amount === undefined || amount === null) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },

    formatHours: function(hours) {
      if (hours === undefined || hours === null) return '0 hrs';
      return hours.toFixed(1) + ' hrs';
    },

    formatNumber: function(number) {
      if (number === undefined || number === null) return '0';
      return new Intl.NumberFormat('en-US').format(number);
    },
  }
};
</script>

<style scoped>
.section-label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
}

.help-text {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.action-bar, .action-bar-bottom {
  display: flex;
  gap: 10px;
  margin: 15px;
  justify-content: flex-end;
}

.workpoint-card {
  margin: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.workpoint-card-content {
  padding: 0;
}

.workpoint-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.workpoint-header h4 {
  margin: 0;
  color: #333;
}

.activities-section {
  padding: 15px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.warning-box {
  background: #fff3e0;
  border-left: 4px solid #ff9800;
  padding: 12px;
  margin-bottom: 15px;
  color: #e65100;
  font-size: 14px;
}

.empty-activities {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-activities md-icon {
  font-size: 48px;
  width: 48px;
  height: 48px;
  margin-bottom: 10px;
}

.shift-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
}

.shift-field {
  margin: 0;
  padding: 0;
  flex: 1;
}

.shift-field .md-input {
  font-size: 14px;
  padding: 8px;
  text-align: center;
}

.provision-checkbox {
  margin: 0 !important;
  transform: scale(0.8);
}

.action-buttons {
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
}

.saved-badge {
  font-size: 11px;
  color: #4caf50;
  background: #e8f5e9;
  padding: 2px 6px;
  border-radius: 4px;
}

.shift-time {
  display: block;
  font-size: 10px;
  color: #666;
  font-weight: normal;
}

.table-footer-row {
  background-color: #f5f5f5;
  font-weight: bold;
}

.cell-50 {
  min-width: 250px;
}

.cell-10 {
  min-width: 100px;
}

.md-button.md-small {
  height: 32px;
  min-height: 32px;
  font-size: 12px;
  padding: 0 12px;
}

.md-button.md-small .md-icon {
  font-size: 18px;
  line-height: 18px;
}

.mt-4 {
  margin-top: 16px;
}

.exception-count {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #ff9800;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.md-button {
  position: relative;
}

.md-button.md-danger .exception-count {
  background-color: #fff;
  color: #f44336;
}
</style>
